import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Save, ImagePlus, Lock, LogOut, Upload, Truck } from 'lucide-react';
import { Layout } from '../components/Layout';
import { 
  getProducts, 
  createProduct, 
  updateProduct, 
  deleteProduct, 
  getCategories,
  adminLogin,
  verifyAdmin,
  adminLogout,
  uploadImage,
  getShippingConfig,
  updateShippingConfig
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const emptyProduct = {
  name: '',
  description: '',
  price: '',
  category: '',
  images: [''],
  colors: [],
  sizes: [],
  color_images: {},
  fabric_details: '',
  care_instructions: '',
  is_featured: false,
  in_stock: true,
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      await adminLogin(username, password);
      toast.success('Login successful');
      onLogin();
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="section-spacing container-luxury" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#F2EFE9] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-[#C6A87C]" />
            </div>
            <h1 className="font-heading text-3xl text-[#2C2420] mb-2">
              Admin Access
            </h1>
            <p className="text-[#5D5550]">
              Please sign in to manage products
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-sm text-[#5D5550]">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1"
                placeholder="Enter username"
                data-testid="admin-username"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm text-[#5D5550]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                placeholder="Enter password"
                data-testid="admin-password"
              />
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="spinner w-4 h-4" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </Layout>
  );
};

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [colorsInput, setColorsInput] = useState('');
  const [sizesInput, setSizesInput] = useState('');
  
  // Shipping config state
  const [shippingConfig, setShippingConfig] = useState({
    free_shipping_threshold: 150,
    rates: { germany: 5, uk: 12, eu: 10, us: 20, rest_of_world: 25 }
  });
  const [savingShipping, setSavingShipping] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const valid = await verifyAdmin();
      setIsAuthenticated(valid);
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const [productsData, categoriesData, shippingData] = await Promise.all([
        getProducts(),
        getCategories(),
        getShippingConfig(),
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
      setShippingConfig(shippingData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleShippingChange = (field, value) => {
    if (field === 'free_shipping_threshold') {
      setShippingConfig(prev => ({ ...prev, free_shipping_threshold: parseFloat(value) || 0 }));
    } else {
      setShippingConfig(prev => ({
        ...prev,
        rates: { ...prev.rates, [field]: parseFloat(value) || 0 }
      }));
    }
  };

  const saveShippingConfig = async () => {
    setSavingShipping(true);
    try {
      await updateShippingConfig({
        free_shipping_threshold: shippingConfig.free_shipping_threshold,
        rates: shippingConfig.rates
      });
      toast.success('Shipping configuration saved');
    } catch (error) {
      console.error('Failed to save shipping config:', error);
      toast.error('Failed to save shipping configuration');
    } finally {
      setSavingShipping(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setColorsInput('');
    setSizesInput('');
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      ...product,
      price: product.price.toString(),
      color_images: product.color_images || {},
    });
    setColorsInput(product.colors.join(', '));
    setSizesInput(product.sizes.join(', '));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setFormData(emptyProduct);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData((prev) => ({ ...prev, images: newImages }));
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, WebP, or GIF)');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    try {
      toast.loading('Uploading image...', { id: 'upload' });
      const result = await uploadImage(file);
      
      const newImages = [...formData.images];
      newImages[index] = result.url;
      setFormData((prev) => ({ ...prev, images: newImages }));
      
      toast.success('Image uploaded successfully', { id: 'upload' });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image', { id: 'upload' });
    }
  };

  const addImageField = () => {
    setFormData((prev) => ({ ...prev, images: [...prev.images, ''] }));
  };

  const removeImageField = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, images: newImages.length ? newImages : [''] }));
  };

  const handleColorImageMapping = (color, imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      color_images: {
        ...prev.color_images,
        [color]: imageUrl,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    const colors = colorsInput.split(',').map((c) => c.trim()).filter(Boolean);
    const images = formData.images.filter((img) => img.trim());
    
    // Clean up color_images to only include valid colors
    const validColorImages = {};
    colors.forEach((color) => {
      if (formData.color_images[color] && images.includes(formData.color_images[color])) {
        validColorImages[color] = formData.color_images[color];
      }
    });

    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      images,
      colors,
      sizes: sizesInput.split(',').map((s) => s.trim()).filter(Boolean),
      color_images: validColorImages,
    };

    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        toast.success('Product updated successfully');
      } else {
        await createProduct(productData);
        toast.success('Product created successfully');
      }
      closeModal();
      loadData();
    } catch (error) {
      console.error('Failed to save product:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        setIsAuthenticated(false);
      } else {
        toast.error('Failed to save product');
      }
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await deleteProduct(productId);
      toast.success('Product deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete product:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        setIsAuthenticated(false);
      } else {
        toast.error('Failed to delete product');
      }
    }
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <Layout>
        <div className="section-spacing flex justify-center items-center min-h-[50vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <Layout>
      <section className="section-spacing" data-testid="admin-page">
        <div className="container-luxury">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-2">
                Product Management
              </h1>
              <p className="text-[#5D5550]">
                Add, edit, or remove products from your catalog
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                className="border-[#EBE5DE]"
                onClick={handleLogout}
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
              <Button
                className="btn-primary"
                onClick={openCreateModal}
                data-testid="add-product-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 bg-[#F2EFE9]">
              <p className="text-[#5D5550] mb-4">No products found</p>
              <Button className="btn-primary" onClick={openCreateModal}>
                Add Your First Product
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-[#F2EFE9] text-xs uppercase tracking-widest text-[#8C8580]">
                <div className="col-span-1">Image</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1">Price</div>
                <div className="col-span-1">Stock</div>
                <div className="col-span-2">Featured</div>
                <div className="col-span-2">Actions</div>
              </div>

              {/* Table Rows */}
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 border border-[#EBE5DE] items-center"
                  data-testid={`product-row-${product.id}`}
                >
                  {/* Image */}
                  <div className="col-span-1">
                    <div className="w-16 h-20 bg-[#F2EFE9] overflow-hidden">
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Name */}
                  <div className="col-span-3">
                    <p className="font-medium text-[#2C2420]">{product.name}</p>
                    <p className="text-xs text-[#8C8580] truncate">{product.id}</p>
                  </div>

                  {/* Category */}
                  <div className="col-span-2">
                    <span className="text-sm text-[#5D5550]">
                      {product.category.replace(/-/g, ' ')}
                    </span>
                  </div>

                  {/* Price */}
                  <div className="col-span-1">
                    <span className="text-sm text-[#2C2420]">€{product.price.toFixed(2)}</span>
                  </div>

                  {/* Stock */}
                  <div className="col-span-1">
                    <span
                      className={`text-xs px-2 py-1 ${
                        product.in_stock
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {product.in_stock ? 'In Stock' : 'Out'}
                    </span>
                  </div>

                  {/* Featured */}
                  <div className="col-span-2">
                    <span
                      className={`text-xs px-2 py-1 ${
                        product.is_featured
                          ? 'bg-[#C6A87C] text-[#2C2420]'
                          : 'bg-[#F2EFE9] text-[#8C8580]'
                      }`}
                    >
                      {product.is_featured ? 'Featured' : 'Standard'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(product)}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(product.id)}
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Shipping Configuration Section */}
      <section className="pb-16" data-testid="shipping-config-section">
        <div className="container-luxury">
          <div className="border-t border-[#EBE5DE] pt-12">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl text-[#2C2420] mb-2 flex items-center gap-3">
                  <Truck className="h-6 w-6 text-[#C6A87C]" />
                  Shipping Configuration
                </h2>
                <p className="text-[#5D5550]">
                  Manage shipping rates for different regions
                </p>
              </div>
              <Button
                className="btn-primary"
                onClick={saveShippingConfig}
                disabled={savingShipping}
                data-testid="save-shipping-btn"
              >
                {savingShipping ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner w-4 h-4" />
                    Saving...
                  </span>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Shipping Config
                  </>
                )}
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Free Shipping Threshold */}
              <div className="bg-[#F9F8F6] p-6 border border-[#EBE5DE]">
                <Label className="text-sm text-[#8C8580] uppercase tracking-widest mb-3 block">
                  Free Shipping Threshold
                </Label>
                <div className="flex items-center gap-3">
                  <span className="text-lg text-[#5D5550]">€</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={shippingConfig.free_shipping_threshold}
                    onChange={(e) => handleShippingChange('free_shipping_threshold', e.target.value)}
                    className="max-w-[150px] text-lg"
                    data-testid="shipping-threshold-input"
                  />
                </div>
                <p className="text-xs text-[#8C8580] mt-2">
                  Orders above this amount qualify for free shipping
                </p>
              </div>

              {/* Shipping Rates */}
              <div className="bg-[#F9F8F6] p-6 border border-[#EBE5DE]">
                <Label className="text-sm text-[#8C8580] uppercase tracking-widest mb-4 block">
                  Regional Shipping Rates
                </Label>
                <div className="space-y-4">
                  {[
                    { key: 'germany', label: 'Germany' },
                    { key: 'uk', label: 'United Kingdom' },
                    { key: 'eu', label: 'EU Countries' },
                    { key: 'us', label: 'United States' },
                    { key: 'rest_of_world', label: 'Rest of World' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-[#2C2420]">{label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#5D5550]">€</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={shippingConfig.rates?.[key] || 0}
                          onChange={(e) => handleShippingChange(key, e.target.value)}
                          className="w-20 text-right"
                          data-testid={`shipping-rate-${key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-6 pt-4 pr-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="modal-product-name"
                />
              </div>

              <div>
                <Label htmlFor="price">Price (€) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  data-testid="modal-product-price"
                />
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger data-testid="modal-product-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                data-testid="modal-product-description"
              />
            </div>

            {/* Images */}
            <div>
              <Label>Product Images</Label>
              {formData.images.map((image, index) => (
                <div key={index} className="mt-3 p-3 border border-[#EBE5DE] rounded-md">
                  <div className="flex gap-2 items-center">
                    <Input
                      value={image}
                      onChange={(e) => handleImageChange(index, e.target.value)}
                      placeholder="Paste image URL or upload below"
                      className="flex-1"
                      data-testid={`modal-product-image-${index}`}
                    />
                    {formData.images.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeImageField(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* File Upload */}
                  <div className="mt-2 flex items-center gap-3">
                    <label className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-[#C6A87C] rounded cursor-pointer hover:bg-[#F2EFE9] transition-colors">
                      <Upload className="h-4 w-4 text-[#C6A87C]" />
                      <span className="text-[#5D5550]">Upload from device</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => handleFileUpload(index, e.target.files[0])}
                        data-testid={`modal-product-upload-${index}`}
                      />
                    </label>
                    {image && (
                      <div className="w-12 h-12 bg-[#F2EFE9] rounded overflow-hidden">
                        <img src={image} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={addImageField}
              >
                <ImagePlus className="h-4 w-4 mr-2" />
                Add Another Image
              </Button>
            </div>

            {/* Colors & Sizes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="colors">Colors (comma-separated)</Label>
                <Input
                  id="colors"
                  value={colorsInput}
                  onChange={(e) => setColorsInput(e.target.value)}
                  placeholder="e.g., Blue, Red, Grey"
                  data-testid="modal-product-colors"
                />
              </div>
              <div>
                <Label htmlFor="sizes">Sizes (comma-separated)</Label>
                <Input
                  id="sizes"
                  value={sizesInput}
                  onChange={(e) => setSizesInput(e.target.value)}
                  placeholder="e.g., S, M, L, XL"
                  data-testid="modal-product-sizes"
                />
              </div>
            </div>

            {/* Color-Image Mapping */}
            {colorsInput && formData.images.filter(img => img.trim()).length > 0 && (
              <div className="border border-[#EBE5DE] rounded-md p-4">
                <Label className="mb-3 block">Map Colors to Images</Label>
                <p className="text-xs text-[#8C8580] mb-4">
                  Select which image should be shown when a customer selects each color.
                </p>
                <div className="space-y-3">
                  {colorsInput.split(',').map((c) => c.trim()).filter(Boolean).map((color) => (
                    <div key={color} className="flex items-center gap-3">
                      <span className="text-sm text-[#2C2420] w-28 truncate">{color}</span>
                      <Select
                        value={formData.color_images[color] || 'none'}
                        onValueChange={(value) => handleColorImageMapping(color, value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select image for this color" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No specific image</SelectItem>
                          {formData.images.filter(img => img.trim()).map((img, idx) => (
                            <SelectItem key={idx} value={img}>
                              <div className="flex items-center gap-2">
                                <span className="truncate max-w-[200px]">Image {idx + 1}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.color_images[color] && (
                        <div className="w-10 h-10 bg-[#F2EFE9] rounded overflow-hidden flex-shrink-0">
                          <img 
                            src={formData.color_images[color]} 
                            alt={color} 
                            className="w-full h-full object-cover" 
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fabric & Care */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fabric_details">Fabric Details</Label>
                <Input
                  id="fabric_details"
                  name="fabric_details"
                  value={formData.fabric_details}
                  onChange={handleInputChange}
                  data-testid="modal-product-fabric"
                />
              </div>
              <div>
                <Label htmlFor="care_instructions">Care Instructions</Label>
                <Input
                  id="care_instructions"
                  name="care_instructions"
                  value={formData.care_instructions}
                  onChange={handleInputChange}
                  data-testid="modal-product-care"
                />
              </div>
            </div>

            {/* Flags */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleInputChange}
                  className="w-4 h-4"
                  data-testid="modal-product-featured"
                />
                <span className="text-sm text-[#5D5550]">Featured Product</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="in_stock"
                  checked={formData.in_stock}
                  onChange={handleInputChange}
                  className="w-4 h-4"
                  data-testid="modal-product-stock"
                />
                <span className="text-sm text-[#5D5550]">In Stock</span>
              </label>
            </div>

            </div>

            {/* Actions - Sticky at bottom */}
            <div className="flex justify-end gap-4 pt-4 mt-4 border-t bg-white sticky bottom-0">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="modal-save-btn">
                <Save className="h-4 w-4 mr-2" />
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
